/**
 * International phone field with country picker and local-format masking.
 */

import * as React from 'react';
import { Label } from '@/components/ui/label';
import { ChevronDown, Check, Search, CheckCircle2, Info, AlertCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  getPhoneFormat,
  formatPhoneByCountry,
  getPhoneValidationState,
  normalizePhoneToLocal,
} from '@/utils/phone-formats-utils';
import { useDefaultPhoneCountry } from '@/utils/user-contact-utils';
import { PHONE_COUNTRIES, type PhoneCountry } from '@/constants/phone-countries-catalog';

/** Props for {@link PhoneInput}. */
interface PhoneInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  labelTooltip?: string;
  error?: string;
  helperText?: string;
  countryCode?: string;
  onCountryChange?: (country: PhoneCountry) => void;
  state?: 'default' | 'error' | 'success' | 'warning';
  inputSize?: 'sm' | 'md' | 'lg';
  /** Node aligned with the control, not the label (contact swap). */
  controlEnd?: React.ReactNode;
}

/** International phone field with country picker and local-format masking. */
export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      label,
      labelTooltip,
      error,
      helperText,
      className,
      id,
      required,
      countryCode,
      onCountryChange,
      value,
      onChange,
      disabled,
      state,
      inputSize = 'md',
      controlEnd,
      ...props
    },
    ref,
  ) => {
    const companyDefaultCountry = useDefaultPhoneCountry();
    // Accepts ISO (e.g. 'FR') or dial code (e.g. '+33'). Falls back to company country from store.
    const effectiveCountryCode = countryCode ?? companyDefaultCountry;

    const inputId = id || `phone-input-${label?.toLowerCase().replaceAll(/\s+/g, '-')}`;
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState('');
    const [selectedCountry, setSelectedCountry] = React.useState<PhoneCountry>(
      PHONE_COUNTRIES.find(
        (c) => c.code === effectiveCountryCode || c.dial === effectiveCountryCode,
      ) ||
        PHONE_COUNTRIES.find((c) => c.code === companyDefaultCountry) ||
        PHONE_COUNTRIES[0],
    );

    React.useEffect(() => {
      if (!effectiveCountryCode) return;
      const match = PHONE_COUNTRIES.find(
        (c) => c.code === effectiveCountryCode || c.dial === effectiveCountryCode,
      );
      if (match && match.code !== selectedCountry.code) {
        setSelectedCountry(match);
      }
    }, [effectiveCountryCode]);
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    const validationState = React.useMemo(() => {
      if (error) return 'error';
      if (state && state !== 'default') return state;

      if (!value || typeof value !== 'string') return 'default';
      const internalState = getPhoneValidationState(String(value), selectedCountry.code);
      return internalState === 'error' ? 'default' : internalState;
    }, [value, selectedCountry.code, state, error]);

    const placeholder = React.useMemo(() => {
      const format = getPhoneFormat(selectedCountry.code);
      return format?.placeholder || '6 12 34 56 78';
    }, [selectedCountry.code]);

    const filteredCountries = React.useMemo(() => {
      if (!searchQuery) return PHONE_COUNTRIES;
      const query = searchQuery.toLowerCase();
      return PHONE_COUNTRIES.filter(
        (c) =>
          c.name.toLowerCase().includes(query) ||
          c.dial.includes(query) ||
          c.code.toLowerCase().includes(query),
      );
    }, [searchQuery]);

    /** Strip non-digits, normalize to local format, then remask. */
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      // Remove all non-digit characters first, then normalize international prefix to local format
      let digitsOnly = inputValue.replaceAll(/\D/g, '');
      digitsOnly = normalizePhoneToLocal(digitsOnly, selectedCountry.code);

      const format = getPhoneFormat(selectedCountry.code);

      if (format && digitsOnly.length > format.maxLength) {
        return;
      }

      // Format according to country
      const formatted = formatPhoneByCountry(digitsOnly, selectedCountry.code);

      const newEvent = {
        ...e,
        target: {
          ...e.target,
          value: formatted,
        },
      } as React.ChangeEvent<HTMLInputElement>;

      onChange?.(newEvent);
    };

    /** Switch country and remask the current digits. */
    const handleCountrySelect = (country: PhoneCountry) => {
      setSelectedCountry(country);
      onCountryChange?.(country);
      setIsOpen(false);
      setSearchQuery('');

      if (value && typeof value === 'string') {
        let digitsOnly = String(value).replaceAll(/\D/g, '');
        digitsOnly = normalizePhoneToLocal(digitsOnly, country.code);
        const reformatted = formatPhoneByCountry(digitsOnly, country.code);

        if (onChange) {
          const syntheticEvent = {
            target: {
              value: reformatted,
            },
          } as React.ChangeEvent<HTMLInputElement>;
          onChange(syntheticEvent);
        }
      }
    };

    /** Trigger height class for the current `inputSize`. */
    const getHeightClass = () => {
      if (inputSize === 'sm') return 'h-9';
      if (inputSize === 'lg') return 'h-12';
      return 'h-11';
    };

    React.useEffect(() => {
      /** Close the country list when the click is outside. */
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
          setSearchQuery('');
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
      <div className="w-full space-y-2">
        {label && (
          <Label htmlFor={inputId} className={cn('form-label', required && 'form-label-required')}>
            {label}
            {labelTooltip && (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex cursor-help ml-1 opacity-50 align-middle">
                      <Info size={13} />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{labelTooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </Label>
        )}
        <div className={controlEnd ? 'flex items-center gap-2' : undefined}>
          <div className={controlEnd ? 'relative min-w-0 flex-1' : 'relative'} ref={dropdownRef}>
            <div
              className={cn(
                'flex items-center rounded-xl border-[1.5px] transition-all duration-200',
                getHeightClass(),
                'border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)]',
                'bg-[rgb(var(--card-rgb)/0.3)] backdrop-blur-[8px]',
                'hover:border-[color-mix(in_srgb,var(--color-1-nuance-2)_70%,transparent)] hover:shadow-sm',
                'focus-within:border-[var(--color-2-nuance-1)]',
                'focus-within:shadow-[0_0_0_4px_color-mix(in_srgb,var(--color-2-nuance-1)_25%,transparent),0_4px_16px_color-mix(in_srgb,var(--color-2-nuance-1)_30%,transparent)]',
                error && 'modern-input-error',
                validationState === 'error' && 'modern-input-error',
                validationState === 'success' && 'modern-input-success',
                validationState === 'warning' && 'modern-input-warning',
                disabled && 'opacity-60 cursor-not-allowed',
              )}
            >
              <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={cn(
                  'flex shrink-0 items-center gap-2 px-3 h-full border-r transition-all duration-300',
                  'border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)]',
                  'hover:bg-[color-mix(in_srgb,var(--color-1-nuance-1)_12%,transparent)]',
                  'rounded-l-xl active:scale-95 whitespace-nowrap',
                )}
              >
                <img
                  src={`https://flagcdn.com/w20/${selectedCountry.code.toLowerCase()}.png`}
                  srcSet={`https://flagcdn.com/w40/${selectedCountry.code.toLowerCase()}.png 2x`}
                  alt={selectedCountry.code}
                  className="w-5 h-3.5 object-cover rounded-[2px] shrink-0"
                />
                <span className="text-sm font-medium">{selectedCountry.dial}</span>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 text-[var(--color-1-nuance-1)] transition-all duration-300',
                    isOpen && 'rotate-180 text-[var(--color-2-nuance-1)]',
                  )}
                />
              </button>

              <input
                ref={ref}
                id={inputId}
                type="tel"
                value={value}
                onChange={handleChange}
                disabled={disabled}
                required={required}
                placeholder={placeholder}
                aria-invalid={!!error}
                aria-describedby={(() => {
                  if (error) return `${inputId}-error`;
                  if (helperText) return `${inputId}-helper`;
                  return undefined;
                })()}
                className={cn(
                  'flex-1 px-4 py-3 bg-transparent border-none outline-none text-base font-medium',
                  getHeightClass(),
                  'placeholder:text-muted-foreground/60 placeholder:font-normal',
                  'disabled:cursor-not-allowed',
                  (error || validationState !== 'default') && 'pr-11',
                  className,
                )}
                {...props}
              />

              {(error || validationState === 'error') && (
                <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[rgb(var(--color-error))]" />
              )}
              {!error && validationState === 'success' && (
                <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[rgb(10, 134, 66)] phone-input-success-icon" />
              )}
            </div>

            {isOpen && (
              <div className="modern-dropdown-content w-full">
                <div className="p-2 border-b border-border/50 mb-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Rechercher un pays..."
                      className="w-full h-9 pl-10 pr-3 rounded-lg border border-border/50 bg-background/50 text-sm focus:outline-none focus:border-[var(--color-2-nuance-1)]"
                    />
                  </div>
                </div>

                <div className="max-h-64 overflow-y-auto scrollbar-thin">
                  {filteredCountries.map((country, index) => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => handleCountrySelect(country)}
                      className={cn(
                        'modern-dropdown-item stagger-item w-full',
                        selectedCountry.code === country.code && 'modern-dropdown-item-selected',
                      )}
                      style={{ '--stagger-index': index } as React.CSSProperties}
                    >
                      <img
                        src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`}
                        srcSet={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png 2x`}
                        alt={country.code}
                        className="w-5 h-3.5 object-cover rounded-[2px] shrink-0 mr-3"
                      />
                      <span className="flex-1 text-left">{country.name}</span>
                      <span className="text-sm text-muted-foreground font-medium">
                        {country.dial}
                      </span>
                      {selectedCountry.code === country.code && (
                        <Check className="h-5 w-5 ml-2 text-[var(--color-2-nuance-1)] animate-in zoom-in-50" />
                      )}
                    </button>
                  ))}
                  {filteredCountries.length === 0 && (
                    <div className="py-8 text-center text-sm text-muted-foreground">
                      Aucun pays trouvé
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {controlEnd ? <div className="shrink-0">{controlEnd}</div> : null}
        </div>
        {error && (
          <p id={`${inputId}-error`} className="form-error-text" role="alert">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={`${inputId}-helper`} className="form-helper-text">
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

PhoneInput.displayName = 'PhoneInput';
